import HubLeadCTA from "@/components/HubLeadCTA";
import NaatiClusterLinks from "@/components/NaatiClusterLinks";
// r24 Wave 2 — NAATI AU cities hub index (TH)
import { createFileRoute, Link } from "@tanstack/react-router";
import { NAATI_AU_CITIES, type AuState } from "@/data/naati-au-cities";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const STATE_TH: Record<AuState, string> = {
  NSW: "นิวเซาท์เวลส์ (NSW)", VIC: "วิกตอเรีย (VIC)", QLD: "ควีนส์แลนด์ (QLD)",
  WA: "เวสเทิร์นออสเตรเลีย (WA)", SA: "เซาท์ออสเตรเลีย (SA)", TAS: "แทสเมเนีย (TAS)",
  ACT: "เขตนครหลวงออสเตรเลีย (ACT)", NT: "นอร์เทิร์นเทร์ริทอรี (NT)",
};
const STATE_ORDER: AuState[] = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

export const Route = createFileRoute("/translate/naati/au/")({
  head: () => {
    const url = `${SITE}/translate/naati/au`;
    const title = `แปล NAATI ส่ง 30 เมืองทั่วออสเตรเลีย — Sydney, Melbourne, Brisbane, Perth`;
    const desc = `รวมบริการแปล NAATI ครอบคลุม ${NAATI_AU_CITIES.length} เมืองใน 8 รัฐทั่วออสเตรเลีย ราคาเดียวจากกรุงเทพ ส่ง DHL Express 3-5 วัน มี hard copy + PDF พร้อม CPN ตรวจสอบได้ใน NAATI Directory — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/naati/au/index", "th") }, { name: "description", content: ctaDesc(desc, "/translate/naati/au/index", "th")},
        { property: "og:title", content: ctaTitle(title, "/translate/naati/au/index", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/naati/au/index", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: canonicalAndHreflang("/translate/naati/au", "/en/translate/naati/au"),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "ItemList", inLanguage: "th",
          numberOfItems: NAATI_AU_CITIES.length,
          itemListElement: NAATI_AU_CITIES.map((c, i) => ({
            "@type": "ListItem", position: i + 1, name: `แปล NAATI ${c.nameTh}`,
            url: `${SITE}/translate/naati/au/${c.slug}`,
          })),
        }) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "แปล NAATI", item: `${SITE}/translate/naati` },
            { "@type": "ListItem", position: 3, name: "เมืองทั่วออสเตรเลีย", item: url },
          ],
        }) },
      ],
    };
  },
  component: AuHub,
});

function AuHub() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
        <Link to="/" className="hover:underline">หน้าแรก</Link> ·{" "}
        <Link to="/translate/naati" className="hover:underline">แปล NAATI</Link> ·{" "}
        <span className="text-slate-700">เมืองในออสเตรเลีย</span>
      </nav>
      <h1 className="text-3xl md:text-4xl font-bold text-navy-deep mb-3">แปล NAATI ส่ง {NAATI_AU_CITIES.length} เมืองทั่วออสเตรเลีย</h1>
      <p data-speakable="true" className="text-lg text-slate-700 mb-8 direct-answer">
        NYC ส่งเอกสารแปล NAATI ถึงมือลูกค้าใน 8 รัฐทั่วออสเตรเลียด้วย DHL Express 3-5 วัน — เลือกเมืองที่ต้องการเพื่อดู
        BDM office ที่ใกล้ที่สุด, tips การยื่นเฉพาะรัฐ และเคสจริงที่ทีมเราจัดการมาก่อน
      </p>
      {STATE_ORDER.map((state) => {
        const cities = NAATI_AU_CITIES.filter((c) => c.state === state);
        if (cities.length === 0) return null;
        return (
          <section key={state} className="mb-10">
            <h2 className="text-2xl font-semibold text-navy-deep border-b border-gold pb-2 mb-4">
              {STATE_TH[state]} <span className="text-sm font-normal text-slate-500">({cities.length} เมือง)</span>
            </h2>
            <ul className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {cities.map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/translate/naati/au/$slug" params={{ slug: c.slug }}
                    className="block border rounded-lg p-3 hover:border-gold hover:bg-amber-50/50 transition"
                  >
                    <div className="font-medium text-navy-deep">{c.nameTh}</div>
                    <div className="text-xs text-slate-500 mt-1">{c.nameEn}{c.isCapital ? " · เมืองหลวงของรัฐ" : ""}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
      <NaatiClusterLinks />
    <HubLeadCTA locale="th" service="NAATI" />
      </main>
  );
}
